import { createAction, handleActions } from 'redux-actions';
import { push } from 'connected-react-router';
import moment from 'moment';

import { offer, enm } from '../services/api';

export const updateOffer = createAction('UPDATE_OFFER', (data) => data);
export const updateFilter = createAction('UPDATE_FILTER', (data) => data);
export const updateCurrentApplicant = createAction('UPDATE_CURRENT_APPLICANT', (data) => data);
export const updateApplicantStatus = createAction('UPDATE_APPLICANT_STATUS', (data) => data);
export const toggleLoading = createAction('TOGGLE_LOADING', (data) => data);
export const moveToNewApplicant = createAction('MOVE_TO_NEW_APPLICANT', (data) => data);
export const updateStatsUnit = createAction('UPDATE_STATS_UNIT', (data) => data);
export const updateSorting = createAction('UPDATE_SORTING', (data) => data);
export const updateMappings = createAction('UPDATE_MAPPINGS', (data) => data);

export interface State {
  offer: Offer | null;
  selectedFilter: ApplyOfferApplicantStatus;
  applicants: Attendee[];
  sorting: OfferSorting;
  currentApplicant: Attendee | null;
  loading: boolean;
  statsInInches: boolean;
  shoeSizeMapping: [];
  suitSizeMapping: [];
  waistSizeMapping: [];
  buckets: [];
}

const initial: State = {
  offer: null,
  selectedFilter: 'unreviewed',
  applicants: [],
  sorting: 'alphabetically',
  currentApplicant: null,
  loading: false,
  statsInInches: true,
  shoeSizeMapping: [],
  suitSizeMapping: [],
  waistSizeMapping: [],
  buckets: [],
};

export function fetchMappings() {
  return async (dispatch: Function) => {
    const shoeSizeMapping: any = await enm.getShoeSizes();
    const suitSizeMapping: any = await enm.getSuitSizes();
    const waistSizeMapping: any = await enm.getWaistSizes();

    dispatch(
      updateMappings({
        shoeSizeMapping: shoeSizeMapping.data,
        suitSizeMapping: suitSizeMapping.data,
        waistSizeMapping: waistSizeMapping.data,
      })
    );
  };
}

export function fetchOffer(offerId: string, filter?: ApplyOfferApplicantStatus) {
  return async (dispatch: Function) => {
    dispatch(toggleLoading({ loading: true }));
    const offerData: any = await offer.getById(offerId, { withAttendees: 'false' });

    const applicantsData: any = await offer.getApplicants(
      offerId,
      getApplicantsObject(filter || 'unreviewed')
    );

    dispatch(
      updateFilter({
        offer: offerData.data,
        applicants: applicantsData.data.applicants.data,
        buckets: applicantsData.data.buckets,
        selectedFilter: filter || 'unreviewed',
      })
    );

    dispatch(toggleLoading({ loading: false }));
  };
}

export function changeFilter(offerId: string, filter: ApplyOfferApplicantStatus) {
  return async (dispatch: Function) => {
    const applicantsData: any = await offer.getApplicants(offerId, getApplicantsObject(filter));

    dispatch(
      updateFilter({
        selectedFilter: filter,
        applicants: applicantsData.data.applicants.data,
        buckets: applicantsData.data.buckets,
      })
    );
  };
}

export function fetchAndSetCurrentApplicant(offerId: string, applicantId: string) {
  return async (dispatch: Function) => {
    const data: any = await offer.getById(offerId, { withAttendees: 'false' });

    // Since we don't know the status of the applicant based on the ID, we need
    // to fetch all buckets and then later figure out which bucket the current
    // applicant belongs to.
    const dismissedApplicantsData: any = await offer.getApplicants(
      offerId,
      getApplicantsObject('dismissed')
    );
    const unreviewedApplicantsData: any = await offer.getApplicants(
      offerId,
      getApplicantsObject('unreviewed')
    );
    const likedApplicantsData: any = await offer.getApplicants(
      offerId,
      getApplicantsObject('liked')
    );
    const selectedApplicantsData: any = await offer.getApplicants(
      offerId,
      getApplicantsObject('selected')
    );
    const allApplicants = [
      ...dismissedApplicantsData.data.applicants,
      ...unreviewedApplicantsData.data.applicants,
      ...likedApplicantsData.data.applicants,
      ...selectedApplicantsData.data.applicants,
    ];
    const currentApplicant = allApplicants.find((attendee) => attendee.id === applicantId);

    let applicantsList;
    switch (currentApplicant.status) {
      case 'dismissed':
        applicantsList = dismissedApplicantsData.data.applicants;
        break;
      case 'unreviewed':
        applicantsList = unreviewedApplicantsData.data.applicants;
        break;
      case 'liked':
        applicantsList = likedApplicantsData.data.applicants;
        break;
      case 'selected':
      default:
        applicantsList = selectedApplicantsData.data.applicants;
        break;
    }

    dispatch(
      updateFilter({
        offer: data.data,
        applicants: applicantsList,
        buckets: unreviewedApplicantsData.data.buckets,
        selectedFilter: currentApplicant.status,
        currentApplicant: currentApplicant,
      })
    );
  };
}

export function setCurrentApplicant(applicant: Attendee) {
  return (dispatch: Function) => {
    dispatch(updateCurrentApplicant({ currentApplicant: applicant }));
  };
}

export function setStatsToInches(setToInches: boolean) {
  return (dispatch: Function) => {
    dispatch(updateStatsUnit({ statsInInches: setToInches }));
  };
}

export function setApplicantStatus(
  offerId: string,
  applicant: any,
  status: ApplyOfferApplicantStatus
) {
  return (dispatch: Function) => {
    offer.setApplicationStatus(applicant.id, status).then(() => {
      dispatch(updateApplicantStatus());
      dispatch(fetchOffer(offerId, applicant.status));
    });
  };
}

export function goToNewApplicant(next: boolean) {
  return (dispatch: Function) => {
    dispatch(moveToNewApplicant(next));
  };
}

export function sortApplicants(sort: OfferSorting) {
  return (dispatch: Function) => {
    dispatch(updateSorting({ sorting: sort }));
  };
}

export function goToOverview(url: string, filter: ApplyOfferApplicantStatus) {
  return (dispatch: Function) => {
    dispatch(toggleLoading({ loading: true }));
    dispatch(push(url, { filter }));
  };
}

export function goToApplicant(url: string, applicant: Attendee) {
  return async (dispatch: Function) => {
    dispatch(setCurrentApplicant(applicant));
    dispatch(push(url, { applicant }));
  };
}

// Helper function to manage sorting of applicants list.
function handleSorting(sort: OfferSorting, applicants: Attendee[]) {
  switch (sort) {
    case 'ig-greatest':
      applicants.sort(
        (a, b) => b.model.instagram?.number_of_followers - a.model.instagram?.number_of_followers
      );
      break;
    case 'ig-fewest':
      applicants.sort(
        (a, b) => a.model.instagram?.number_of_followers - b.model.instagram?.number_of_followers
      );
      break;
    case 'most-recent':
      applicants.sort((a, b) => (moment(a.claimed_at).isBefore(b.claimed_at) ? 1 : -1));
      break;
    case 'least-recent':
      applicants.sort((a, b) => (moment(a.claimed_at).isAfter(b.claimed_at) ? 1 : -1));
      break;
    case 'alphabetically-reversed':
      applicants.sort((a, b) => a.model.name.localeCompare(b.model.name)).reverse();
      break;
    case 'alphabetically':
    default:
      applicants.sort((a, b) => a.model.name.localeCompare(b.model.name));
      break;
  }

  return applicants;
}

function getApplicantsObject(filter: ApplyOfferApplicantStatus) {
  return {
    status: filter || 'unreviewed',
    page: 0,
    per_page: 1000,
  };
}

export default handleActions(
  {
    [updateOffer](state: State, { payload }): State {
      return { ...state, ...payload };
    },
    [updateSorting](state: State, { payload }): State {
      const newApplicants = handleSorting(payload['sorting'], state.applicants);

      return { ...state, ...payload, applicants: newApplicants };
    },
    [updateFilter](state: State, { payload }): State {
      const newApplicants = payload.applicants
        ? handleSorting(state.sorting, payload.applicants)
        : state.applicants;

      return { ...state, ...payload, applicants: newApplicants };
    },
    [updateCurrentApplicant](state: State, { payload }): State {
      return { ...state, ...payload };
    },
    // Called when a button is clicked to update the application status of an
    // attendee. When this happens, we want to update the list of current
    // applicants, as well as set a new applicant. If this is the last person in
    // the list, the applicants page will redirect back to the overview page.
    [updateApplicantStatus](state: State, { payload }): State {
      // Get the index in the applicants array of the current applicant.
      let index = 0;
      if (state.currentApplicant) {
        index = state.applicants.map((a) => a.id).indexOf(state.currentApplicant.id);
      }

      // Remove the updated applicant from the current list.
      const newApplicants = state.applicants;
      newApplicants.splice(index, 1);

      let newCurrentApplicant;
      if (newApplicants.length > 0) {
        // Set the current applicant to the next applicant in the list.
        index = index === newApplicants.length ? index - 1 : index;
        newCurrentApplicant = newApplicants[index];

        // Update the url to match the current selected attendee.
        window.history.replaceState(
          null,
          '',
          `/offer/apply/${state.offer?.id}/applicant/${newCurrentApplicant.id}`
        );
      }

      return {
        ...state,
        ...payload,
        applicants: newApplicants,
        currentApplicant: newCurrentApplicant,
      };
    },
    [toggleLoading](state: State, { payload }): State {
      return { ...state, ...payload };
    },
    [updateStatsUnit](state: State, { payload }): State {
      return { ...state, ...payload };
    },
    [updateMappings](state: State, { payload }): State {
      return { ...state, ...payload };
    },
    [moveToNewApplicant](state: State, { payload }): State {
      // Get the index in the applicants array of the current applicant.
      let index = 0;
      if (state.currentApplicant) {
        index = state.applicants.map((a) => a.id).indexOf(state.currentApplicant.id);
      }

      // Move the index on space to the left or right depending on the payload.
      index = payload ? index + 1 : index - 1;
      // Moving Previous on the first item takes you to the last item.
      if (index < 0) index = state.applicants.length - 1;
      // Moving Next on the last item takes you to the first item.
      else if (index === state.applicants.length) index = 0;

      const newCurrentApplicant = state.applicants[index];

      // Update the url to match the current selected attendee.
      window.history.replaceState(
        null,
        '',
        `/offer/apply/${state.offer?.id}/applicant/${newCurrentApplicant.id}`
      );

      return {
        ...state,
        currentApplicant: newCurrentApplicant,
      };
    },
  },
  initial
);
